import React from "react";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { MONO_FONT, headCellSx, numCellSx, textCellSx } from "../../components/metrics/metricsUi";
import { COVERAGE_LABELS, RISK_LABELS, fmtDay, fmtMoney, fmtUnits } from "../../utils/commandCenterPageDefaults";

const StockDetail = ({ ui, rows }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell sx={headCellSx(ui)}>SKU</TableCell>
        <TableCell sx={headCellSx(ui)}>Status</TableCell>
        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>On hand</TableCell>
        <TableCell sx={headCellSx(ui)}>Runs out</TableCell>
        <TableCell sx={headCellSx(ui)}>On order</TableCell>
        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>Sales/day</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row) => (
        <TableRow key={row.sku}>
          <TableCell sx={textCellSx(ui, { whiteSpace: "nowrap" })}>
            <span style={{ fontFamily: MONO_FONT, fontSize: 12 }}>{row.sku}</span>
            {(row.by_warehouse || []).length ? (
              <div style={{ fontSize: 11, color: ui.colors.textTertiary, marginTop: 2 }}>
                {row.by_warehouse.map((entry) => `${entry.warehouse_name || "—"} ${fmtUnits(entry.on_hand)}`).join(" · ")}
              </div>
            ) : null}
          </TableCell>
          <TableCell sx={textCellSx(ui, { whiteSpace: "nowrap" })}>{RISK_LABELS[row.risk] || row.risk}</TableCell>
          <TableCell sx={numCellSx(ui)}>{fmtUnits(row.on_hand)}</TableCell>
          <TableCell sx={textCellSx(ui, { whiteSpace: "nowrap" })}>{fmtDay(row.stockout_date)}</TableCell>
          <TableCell sx={textCellSx(ui, { whiteSpace: "nowrap" })}>
            {COVERAGE_LABELS[row.coverage] || row.coverage}
            {row.next_po_no ? (
              <div style={{ fontFamily: MONO_FONT, fontSize: 11, color: ui.colors.textTertiary, marginTop: 2 }}>{row.next_po_no}</div>
            ) : null}
          </TableCell>
          <TableCell sx={numCellSx(ui)}>{fmtMoney(row.per_day)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const PoDetail = ({ ui, rows }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell sx={headCellSx(ui)}>SKU</TableCell>
        <TableCell sx={headCellSx(ui)}>Product</TableCell>
        <TableCell sx={headCellSx(ui)}>Status</TableCell>
        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>On hand</TableCell>
        <TableCell sx={headCellSx(ui)}>Runs out</TableCell>
        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>Sales/day</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row) => (
        <TableRow key={row.sku}>
          <TableCell sx={textCellSx(ui, { fontFamily: MONO_FONT, fontSize: 12 })}>{row.sku}</TableCell>
          <TableCell sx={textCellSx(ui)}>{row.product_name || "—"}</TableCell>
          <TableCell sx={textCellSx(ui)}>{RISK_LABELS[row.risk] || row.risk}</TableCell>
          <TableCell sx={numCellSx(ui)}>{fmtUnits(row.on_hand)}</TableCell>
          <TableCell sx={textCellSx(ui)}>{fmtDay(row.stockout_date)}</TableCell>
          <TableCell sx={numCellSx(ui)}>{fmtMoney(row.per_day)}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const OrderDetail = ({ ui, rows }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell sx={headCellSx(ui)}>Order</TableCell>
        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>Days open</TableCell>
        <TableCell sx={headCellSx(ui)}>Channel</TableCell>
        <TableCell sx={headCellSx(ui, { textAlign: "right" })}>Value</TableCell>
        <TableCell sx={headCellSx(ui)}>Why</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row) => (
        <TableRow key={row.order_id}>
          <TableCell sx={textCellSx(ui, { fontFamily: MONO_FONT, fontSize: 12 })}>{row.order_id}</TableCell>
          <TableCell sx={numCellSx(ui)}>{fmtUnits(row.age_days)}</TableCell>
          <TableCell sx={textCellSx(ui)}>{row.channel || "—"}</TableCell>
          <TableCell sx={numCellSx(ui)}>{fmtMoney(row.value)}</TableCell>
          <TableCell sx={textCellSx(ui, { fontSize: 12, color: ui.colors.textSecondary })}>{row.reason}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export const detailRows = (item) => (item.signal === "orders" ? item.detail?.orders : item.detail?.skus) || [];

/** The rows behind one item: its SKUs, or its orders. */
const DetailTable = ({ ui, item }) => {
  const rows = detailRows(item);
  if (!rows.length) return null;
  if (item.signal === "orders") return <OrderDetail ui={ui} rows={rows} />;
  if (item.signal === "po") return <PoDetail ui={ui} rows={rows} />;
  return <StockDetail ui={ui} rows={rows} />;
};

export default DetailTable;
